import React from 'react';
import { ArrowDown, MousePointer, Users, Eye, Heart, Send, UserPlus } from 'lucide-react';

const FunnelChart = ({ funnelData, onStageClick }) => {
    if (!funnelData || funnelData.length === 0) {
        return (
            <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Conversion Funnel</h3>
                <p className="text-gray-500">No funnel data available</p>
            </div>
        );
    }

    const maxCount = Math.max(...funnelData.map(stage => stage.count));

    const getEventNameFromStage = (stageName) => {
        const mapping = {
            'Signup': 'signup_completed',
            'Property Viewed': 'property_viewed',
            'Property Saved': 'property_saved',
            'Enquiry Sent': 'enquiry_sent',
        };
        return mapping[stageName] || null;
    };

    const getStageIcon = (stageName) => {
        const icons = {
            'Signup': UserPlus,
            'Property Viewed': Eye,
            'Property Saved': Heart,
            'Enquiry Sent': Send,
        };
        return icons[stageName] || Users;
    };

    const getStageColor = (index) => {
        // Use consistent brand color #55C5D0 for all stages
        return 'bg-[#55C5D0]';
    };

    return (
        <div className="bg-white rounded-lg shadow-lg p-6 border border-gray-100">
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h3 className="text-xl font-bold text-gray-900">Conversion Funnel</h3>
                    <p className="text-sm text-gray-500 mt-1">Track buyer journey from signup to enquiry</p>
                </div>
                <p className="text-xs text-gray-500 bg-[#55C5D0] bg-opacity-10 text-[#55C5D0] px-3 py-1 rounded-full">Click stages to view details</p>
            </div>

            <div className="space-y-6">
                {funnelData.map((stage, index) => {
                    const widthPercentage = maxCount > 0 ? (stage.count / maxCount) * 100 : 0;
                    const isFirst = index === 0;
                    const eventName = getEventNameFromStage(stage.stage);
                    const isClickable = eventName && onStageClick;
                    const StageIcon = getStageIcon(stage.stage);
                    const gradientColor = getStageColor(index);

                    return (
                        <div key={index} className="relative">
                            {!isFirst && (
                                <div className="flex justify-center my-3">
                                    <div className="flex flex-col items-center">
                                        <ArrowDown className="w-6 h-6 text-gray-300" />
                                        {stage.conversion_rate !== null && (
                                            <span className="text-xs font-semibold mt-1 px-2 py-0.5 rounded bg-[#55C5D0] bg-opacity-10 text-[#55C5D0]">
                                                {stage.conversion_rate}% conversion
                                            </span>
                                        )}
                                    </div>
                                </div>
                            )}
                            <div
                                className={`relative group ${isClickable ? 'cursor-pointer' : ''}`}
                                onClick={() => isClickable && onStageClick(eventName, stage.stage)}
                            >
                                {/* Stage Header */}
                                <div className="flex items-center justify-between mb-3">
                                    <div className="flex items-center gap-3">
                                        <div className={`p-2 rounded-lg bg-gray-100 text-gray-400 shadow-sm transition-colors group-hover:bg-gray-200`}>
                                            <StageIcon className="w-5 h-5" />
                                        </div>
                                        <div>
                                            <h4 className="text-base font-semibold text-gray-900">{stage.stage}</h4>
                                            {isClickable && (
                                                <p className="text-xs text-[#55C5D0] opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1 mt-0.5 font-medium">
                                                    <MousePointer className="w-3 h-3" />
                                                    Click to view details
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-4">
                                        <div className="text-right">
                                            <div className="text-2xl font-bold text-gray-900">
                                                {stage.count.toLocaleString()}
                                            </div>
                                            <div className="text-xs text-gray-500">buyers</div>
                                        </div>
                                    </div>
                                </div>

                                {/* Progress Bar - Reduced height from h-12 to h-6 */}
                                <div className={`relative w-full bg-gray-100 rounded-full h-6 overflow-hidden shadow-inner ${isClickable ? 'group-hover:ring-2 group-hover:ring-[#55C5D0] group-hover:ring-opacity-30 transition-all' : ''}`}>
                                    <div
                                        className={`${gradientColor} h-6 rounded-full flex items-center justify-between px-3 transition-all duration-700 ease-out shadow-sm`}
                                        style={{ width: `${widthPercentage}%` }}
                                    >
                                        <div className="flex items-center gap-2">
                                            {widthPercentage > 15 && (
                                                <span className="text-white text-[10px] font-bold">
                                                    {stage.count.toLocaleString()}
                                                </span>
                                            )}
                                        </div>
                                        {widthPercentage > 25 && stage.conversion_rate !== null && (
                                            <span className="text-white text-[10px] font-bold bg-white/20 px-1.5 py-0.5 rounded-full">
                                                {stage.conversion_rate}%
                                            </span>
                                        )}
                                    </div>
                                    {widthPercentage <= 15 && (
                                        <div className="absolute inset-0 flex items-center px-3">
                                            <span className="text-gray-600 text-[10px] font-bold">
                                                {stage.count.toLocaleString()}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default FunnelChart;
